import type { Solicitacao, HistoricoStatus, Configuracao } from '@/types'
import { formatarDataHora } from './protocolo'

// ── Constantes de layout A4 ─────────────────────────────────────────────────
const PAGE_W  = 210
const PAGE_H  = 297
const MARGIN  = 14
const COL_GAP = 6

// ── Paleta institucional azul ────────────────────────────────────────────────
const C_AZUL_ESCURO  = '#0f3b50'
const C_AZUL         = '#0f3b50'
const C_AZUL_MEDIO   = '#1a5470'
const C_AZUL_CLARO   = '#e8f1f5'
const C_AZUL_BORDA   = '#b8d4e0'
const C_CINZA_900    = '#111827'
const C_CINZA_700    = '#374151'
const C_CINZA_500    = '#6b7280'
const C_CINZA_200    = '#e5e7eb'
const C_CINZA_100    = '#f3f4f6'
const C_BRANCO       = '#ffffff'

// ── Tipo mínimo do jsPDF ────────────────────────────────────────────────────
type Doc = {
  setFont: (n: string, s?: string) => void
  setFontSize: (s: number) => void
  setTextColor: (...a: any[]) => void
  setFillColor: (...a: any[]) => void
  setDrawColor: (...a: any[]) => void
  setLineWidth: (w: number) => void
  text: (t: string | string[], x: number, y: number, o?: any) => void
  rect: (x: number, y: number, w: number, h: number, s?: string) => void
  line: (x1: number, y1: number, x2: number, y2: number) => void
  addImage: (d: string, f: string, x: number, y: number, w: number, h: number) => void
  addPage: () => void
  save: (n: string) => void
  getNumberOfPages: () => number
  setPage: (n: number) => void
  splitTextToSize: (t: string, w: number) => string[]
  internal: { pageSize: { width: number; height: number } }
}

// ── Utilitários de cor ──────────────────────────────────────────────────────
function fromHex(h: string): [number, number, number] {
  return [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)]
}
const fill  = (d: Doc, c: string) => d.setFillColor(...fromHex(c))
const draw  = (d: Doc, c: string) => d.setDrawColor(...fromHex(c))
const color = (d: Doc, c: string) => d.setTextColor(...fromHex(c))

// ── URL da logo GEOA (hosted) ─────────────────────────────────────────────────
const GEOA_LOGO_URL = 'https://file.lumi.new/p455908949713027072/SPF2065453480390553600.jpg'

// ── Converte URL de imagem para dataURL via canvas (resolve fundo preto/transparência) ──
function loadImageAsDataURL(url: string, bgColor = '#ffffff'): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width  = img.naturalWidth  || img.width
      canvas.height = img.naturalHeight || img.height
      const ctx = canvas.getContext('2d')!
      // Preenche fundo branco para eliminar transparência/preto
      ctx.fillStyle = bgColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error(`Falha ao carregar imagem: ${url}`))
    img.src = url
  })
}

// ── Cabeçalho institucional (modelo prefeitura + GEOA) ───────────────────────
function drawHeader(doc: Doc, cfg: Configuracao | null, titulo: string, pagina: number, geoaDataURL?: string) {
  const W = PAGE_W
  const M = MARGIN

  // ── Alturas do cabeçalho ──
  const HDR_H   = 33   // altura da faixa principal
  const SUB_H   = 7.5  // altura da faixa de título

  // ── Fundo branco do cabeçalho ──
  fill(doc, C_BRANCO)
  doc.rect(0, 0, W, HDR_H + SUB_H + 1.2, 'F')

  // ── Logo da Prefeitura (esquerda) ──
  const logoSize = 22
  const logoX    = M
  const logoY    = (HDR_H - logoSize) / 2  // centralizado verticalmente

  if (cfg?.logoUrl) {
    try {
      doc.addImage(cfg.logoUrl, 'PNG', logoX, logoY, logoSize, logoSize)
    } catch {
      try {
        doc.addImage(cfg.logoUrl, 'JPEG', logoX, logoY, logoSize, logoSize)
      } catch { /* logo indisponível */ }
    }
  } else {
    fill(doc, C_AZUL_CLARO)
    draw(doc, C_AZUL_BORDA)
    doc.setLineWidth(0.1)
    doc.rect(logoX, logoY, logoSize, logoSize, 'FD')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    color(doc, C_AZUL_ESCURO)
    doc.text('PM', logoX + logoSize / 2, logoY + logoSize / 2 + 1.5, { align: 'center' })
  }

  // ── Logo GEOA (direita) — caixa com borda discreta ──
  // Proporção original da imagem: ~2.4:1 (largura:altura) — mantemos para não distorcer
  const geoa_W   = 37
  const geoa_H   = 15   // proporcional à imagem real (~2.4:1)
  const geoa_X   = W - M - geoa_W
  const geoa_Y   = (HDR_H - geoa_H) / 2

  // Fundo branco explícito + borda da caixa GEOA
  fill(doc, C_BRANCO)
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.4)
  doc.rect(geoa_X - 2, geoa_Y - 2, geoa_W + 4, geoa_H + 4, 'FD')

  // Imagem GEOA — usa dataURL pré-processado pelo canvas (sem fundo preto)
  if (geoaDataURL) {
    try {
      doc.addImage(geoaDataURL, 'PNG', geoa_X, geoa_Y, geoa_W, geoa_H)
    } catch { /* fallback abaixo */ }
  }
  if (!geoaDataURL) {
    // fallback texto quando imagem não carregou
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    color(doc, C_AZUL_ESCURO)
    doc.text('GEOA', geoa_X + geoa_W / 2, geoa_Y + geoa_H / 2 + 1, { align: 'center' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(5.5)
    color(doc, C_AZUL_ESCURO)
    doc.text('Gestão Online Ambiental', geoa_X + geoa_W / 2, geoa_Y + geoa_H / 2 + 5.5, { align: 'center' })
  }

  // ── Textos institucionais (entre as duas logos) ──
  const textX   = logoX + logoSize + 5
  const textMaxW = geoa_X - 4 - textX  // não ultrapassa a logo GEOA

  // Nome da prefeitura — bold azul escuro
  const nomePref = (cfg?.nomePrefeitura || 'PREFEITURA MUNICIPAL DE SÃO JOSÉ DO SERIDÓ').toUpperCase()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  color(doc, C_AZUL_ESCURO)
  doc.text(nomePref, textX, 9, { maxWidth: textMaxW })

  // Secretaria
  const secretaria = cfg?.bairro
    ? `Secretaria Municipal – ${cfg.bairro}`
    : 'Secretaria Municipal de Meio Ambiente'
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_700)
  doc.text(secretaria, textX, 15)

  // Endereço + CEP
  const endParts: string[] = []
  if (cfg?.endereco) endParts.push(cfg.endereco)
  if (cfg?.cep)      endParts.push(`CEP: ${cfg.cep}`)
  const endLine = endParts.length ? endParts.join('   |   ') : ''
  if (endLine) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    color(doc, C_CINZA_500)
    doc.text(endLine, textX, 20.5, { maxWidth: textMaxW })
  }

  // CNPJ + Telefone + E-mail
  const contatos: string[] = []
  if (cfg?.cnpj)     contatos.push(`CNPJ: ${cfg.cnpj}`)
  if (cfg?.telefone) contatos.push(`Tel: ${cfg.telefone}`)
  if (cfg?.email)    contatos.push(cfg.email)
  if (contatos.length) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    color(doc, C_CINZA_500)
    doc.text(contatos.join('   |   '), textX, 26, { maxWidth: textMaxW })
  }

  // ── Linha separadora azul (base do cabeçalho) ──
  fill(doc, C_AZUL_ESCURO)
  doc.rect(0, HDR_H, W, 1.2, 'F')

  // ── Faixa de título do documento ──
  fill(doc, C_AZUL_CLARO)
  doc.rect(0, HDR_H + 1.2, W, SUB_H, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.8)
  color(doc, C_AZUL_ESCURO)
  doc.text('GEOA – Gestão Online Ambiental  |  Sistema de Remoção de Árvores  –  São José do Seridó/RN', W / 2, HDR_H + 1.2 + SUB_H - 2, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(5.8)
  color(doc, C_CINZA_500)
  doc.text(`${titulo}  •  Pág. ${pagina}`, W - M, HDR_H + 1.2 + SUB_H - 2, { align: 'right' })
}

// ── Rodapé ──────────────────────────────────────────────────────────────────
function drawFooter(doc: Doc, protocolo: string, pg: number, total: number) {
  const pageH = PAGE_H

  // Linha azul fina no topo do rodapé
  fill(doc, C_AZUL_ESCURO)
  doc.rect(0, pageH - 11, PAGE_W, 0.8, 'F')

  fill(doc, C_AZUL_CLARO)
  doc.rect(0, pageH - 10.2, PAGE_W, 10.2, 'F')

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.5)
  color(doc, C_CINZA_500)
  doc.text(`Protocolo: ${protocolo}`, MARGIN + 2, pageH - 4)
  doc.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, PAGE_W / 2, pageH - 4, { align: 'center' })
  doc.text(`Página ${pg} de ${total}`, PAGE_W - MARGIN, pageH - 4, { align: 'right' })
}

// ── Título de secção moderno com cantos arredondados ─────────────────────────
function secTitle(doc: Doc, title: string, y: number): number {
  const W   = PAGE_W
  const bW  = W - MARGIN * 2
  const bH  = 7

  // Fundo azul arredondado
  fill(doc, C_AZUL_ESCURO)
  roundedRect(doc, MARGIN, y, bW, bH, 2, 'F')

  // Decoração: pequeno quadrado branco à esquerda
  doc.setFillColor(255, 255, 255)
  doc.setGState && (doc as any).setGState && true
  doc.rect(MARGIN + 4, y + 2, 1.5, 3, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.8)
  doc.setTextColor(255, 255, 255)
  doc.text(title, MARGIN + 8, y + 4.8)
  return y + bH + 4
}

// ── Desenha retângulo com cantos arredondados ───────────────────────────────
function roundedRect(doc: Doc, x: number, y: number, w: number, h: number, r: number, style: string) {
  const x2 = x + w
  const y2 = y + h
  const k  = r * 0.5523
  // Tracing path manually using lines + bezier approximation via rect with clip trick:
  // jsPDF >=2.x has roundedRect built-in; use it if available, else fallback
  const d = doc as any
  if (typeof d.roundedRect === 'function') {
    d.roundedRect(x, y, w, h, r, r, style)
  } else {
    // fallback: simple rect
    doc.rect(x, y, w, h, style)
  }
  void x2; void y2; void k
}

// ── Campo rótulo + valor com caixinha arredondada moderna ────────────────────
function field(doc: Doc, label: string, value: string, x: number, y: number, maxW = 80): number {
  const lines   = doc.splitTextToSize(value || '—', maxW - 6)
  const boxH    = lines.length * 3.8 + 10
  const r       = 2   // raio dos cantos

  // Sombra sutil (caixa levemente deslocada, cor cinza muito clara)
  fill(doc, '#eef2f5')
  doc.setLineWidth(0)
  roundedRect(doc, x + 0.4, y + 0.4, maxW, boxH, r, 'F')

  // Caixa de fundo principal
  fill(doc, '#f8fafc')
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.2)
  roundedRect(doc, x, y, maxW, boxH, r, 'FD')

  // Acento esquerdo azul arredondado (apenas lado esq.)
  fill(doc, C_AZUL_ESCURO)
  roundedRect(doc, x, y, 2.2, boxH, 1, 'F')
  // Cobrir lado direito do acento com cor de fundo (simula acento só na esquerda)
  fill(doc, '#f8fafc')
  doc.rect(x + 1.5, y + 0.5, 1.2, boxH - 1, 'F')

  // Label em azul médio
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(5.2)
  color(doc, C_AZUL_MEDIO)
  doc.text(label.toUpperCase(), x + 5, y + 4)

  // Linha separadora label/valor
  draw(doc, C_CINZA_200)
  doc.setLineWidth(0.08)
  doc.line(x + 5, y + 5.5, x + maxW - 3, y + 5.5)

  // Valor
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_900)
  doc.text(lines, x + 5, y + 8.5)

  return y + boxH + 2.5
}

// ── Caixa de protocolo em destaque ───────────────────────────────────────────
function drawProtocoloBadge(doc: Doc, sol: Solicitacao, y: number): number {
  const W = PAGE_W
  const M = MARGIN
  const boxH = 18

  // Fundo azul escuro
  fill(doc, C_AZUL_ESCURO)
  doc.rect(M, y, W - M * 2, boxH, 'F')

  // Protocolo centralizado em branco
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(255, 255, 255)
  doc.text(`Protocolo Nº ${sol.protocolo}`, W / 2, y + 7.5, { align: 'center' })

  // Linha separadora interna
  doc.setDrawColor(255, 255, 255)
  doc.setLineWidth(0.1)
  doc.line(M + 20, y + 10, W - M - 20, y + 10)

  // Status + data em branco suave
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6.8)
  doc.setTextColor(180, 210, 225)
  doc.text(
    `Abertura: ${formatarDataHora(sol.createdAt)}     Status: ${sol.status}`,
    W / 2, y + 14.5, { align: 'center' }
  )
  return y + boxH + 5
}

// ── Bloco de assinaturas físicas — apenas Solicitante + Responsável Técnico ──
function drawAssinaturas(doc: Doc, sol: Solicitacao, y: number): number {
  const M   = MARGIN
  const W   = PAGE_W
  const tw  = W - M * 2
  const gap = 16
  const sw  = (tw - gap) / 2   // apenas 2 assinaturas
  const lh  = 22
  const tg  = 5

  const x1 = M
  const x2 = M + sw + gap

  draw(doc, C_AZUL_ESCURO)
  doc.setLineWidth(0.4)

  // Linha 1 — Solicitante
  doc.line(x1, y + lh, x1 + sw, y + lh)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  color(doc, C_CINZA_900)
  doc.text(sol.nomeCompleto || '________________________________', x1 + sw / 2, y + lh + tg, { align: 'center' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6)
  color(doc, C_CINZA_500)
  doc.text(`CPF: ${sol.cpf || '___.___.___-__'}`, x1 + sw / 2, y + lh + tg + 4, { align: 'center' })
  doc.text('Solicitante', x1 + sw / 2, y + lh + tg + 8, { align: 'center' })

  // Linha 2 — Responsável Técnico
  doc.line(x2, y + lh, x2 + sw, y + lh)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7)
  color(doc, C_CINZA_900)
  doc.text('________________________________', x2 + sw / 2, y + lh + tg, { align: 'center' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6)
  color(doc, C_CINZA_500)
  doc.text('Responsável Técnico', x2 + sw / 2, y + lh + tg + 4, { align: 'center' })
  doc.text('Secretaria Municipal de Meio Ambiente', x2 + sw / 2, y + lh + tg + 8, { align: 'center' })

  return y + lh + tg + 14
}

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÃO PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export async function gerarPDF(
  sol: Solicitacao,
  historico: HistoricoStatus[],
  cfg: Configuracao | null,
) {
  const { default: JsPDF } = await import('jspdf')
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as unknown as Doc

  const M        = MARGIN
  const W        = PAGE_W
  const CONTENT_Y = 46
  const COL_W    = (W - M * 2 - COL_GAP) / 2

  const totalPages = 3
  const fotos = sol.fotos?.filter(Boolean) || []

  // ── Pré-carregar logo GEOA via canvas para eliminar fundo preto ──
  let geoaDataURL: string | undefined
  try {
    geoaDataURL = await loadImageAsDataURL(GEOA_LOGO_URL, '#ffffff')
  } catch {
    geoaDataURL = undefined
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PÁGINA 1 — Dados do Processo e do Solicitante
  // ─────────────────────────────────────────────────────────────────────────
  drawHeader(doc, cfg, 'Dados do Processo e do Solicitante', 1, geoaDataURL)
  let y = CONTENT_Y

  y = drawProtocoloBadge(doc, sol, y)

  // Dados do solicitante
  y = secTitle(doc, 'DADOS DO SOLICITANTE', y)
  let y1 = y, y2 = y
  y1 = field(doc, 'Nome Completo', sol.nomeCompleto, M, y1, COL_W)
  y2 = field(doc, 'CPF', sol.cpf, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  y1 = field(doc, 'Telefone', sol.telefone, M, y1, COL_W)
  y2 = field(doc, 'Número', sol.numero, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  y1 = field(doc, 'Bairro', sol.bairroNome, M, y1, COL_W)
  y2 = field(doc, 'Rua', sol.ruaNome, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  const cidadeSol = (sol as any).cidadeSolicitante || '—'
  const cepSol    = (sol as any).cep || '—'
  y1 = field(doc, 'CEP', cepSol, M, y1, COL_W)
  y2 = field(doc, 'Cidade do Solicitante', cidadeSol, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y = field(doc, 'Ponto de Referência', sol.pontoReferencia || '—', M, y, W - M * 2)
  y += 5

  drawFooter(doc, sol.protocolo, 1, totalPages)

  // ─────────────────────────────────────────────────────────────────────────
  // PÁGINA 2 — Árvore, Relato e Fotos
  // ─────────────────────────────────────────────────────────────────────────
  doc.addPage()
  drawHeader(doc, cfg, 'Informações da Árvore, Relato e Fotografias', 2, geoaDataURL)
  y = CONTENT_Y

  y = secTitle(doc, 'DADOS DA ÁRVORE', y)
  y1 = y; y2 = y
  y1 = field(doc, 'Tipo da Árvore', sol.tipoArvore, M, y1, COL_W)
  y2 = field(doc, 'Quantidade', String(sol.quantidadeArvores), M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  y1 = field(doc, 'Porte da Árvore', (sol as any).porte || '—', M, y1, COL_W)
  y2 = field(doc, 'Estado da Árvore', sol.estadoArvore, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  y1 = field(doc, 'Será Substituída?', sol.seraSubstituida ? 'Sim' : 'Não', M, y1, COL_W)
  if (sol.seraSubstituida) {
    y2 = field(doc, 'Local do Replantio', sol.localReplantio === 'mesmo' ? 'Mesmo Local' : 'Outro Local', M + COL_W + COL_GAP, y2, COL_W)
  }
  y = Math.max(y1, y2) + 2
  if (sol.seraSubstituida) {
    const qualArvore = (sol as any).qualArvoreReplantio
    if (qualArvore) {
      y = field(doc, 'Árvore a ser Plantada', qualArvore, M, y, W - M * 2)
      y += 2
    }
    if (sol.localReplantio === 'outro' && sol.ondeReplantio) {
      y = field(doc, 'Onde Será o Replantio', sol.ondeReplantio, M, y, W - M * 2)
      y += 2
    }
  }
  if (!sol.seraSubstituida) {
    const motivo = (sol as any).motivoNaoSubstituicao
    if (motivo) {
      y = field(doc, 'Motivo da Não Substituição', motivo, M, y, W - M * 2)
      y += 2
    }
    // Nota de taxa de compensação ambiental
    const notaH = 18
    fill(doc, '#fff7ed')
    draw(doc, '#fed7aa')
    doc.setLineWidth(0.3)
    doc.rect(M, y, W - M * 2, notaH, 'FD')
    fill(doc, '#ea580c')
    doc.rect(M, y, 2.5, notaH, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(154, 52, 18)
    doc.text('ATENÇÃO – TAXA DE COMPENSAÇÃO AMBIENTAL', M + 6, y + 5)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(180, 83, 9)
    const taxaLines = doc.splitTextToSize(
      'O não plantio de uma árvore para substituir uma árvore removida pode gerar a cobrança de uma taxa de compensação ambiental, conforme previsto na legislação municipal. Recomenda-se o replantio para evitar encargos adicionais.',
      W - M * 2 - 12
    )
    doc.text(taxaLines, M + 6, y + 10)
    y += notaH + 4
  }
  y += 4

  y = secTitle(doc, 'RELATO DO PROBLEMA', y)
  // Caixa de fundo para o relato
  const relatoLines = doc.splitTextToSize(sol.relatoProblema || '—', W - M * 2 - 8)
  const relatoH = relatoLines.length * 3.8 + 8
  fill(doc, C_CINZA_100)
  draw(doc, C_CINZA_200)
  doc.setLineWidth(0.1)
  doc.rect(M, y, W - M * 2, relatoH, 'FD')
  fill(doc, C_AZUL)
  doc.rect(M, y, 2.5, relatoH, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  color(doc, C_CINZA_700)
  doc.text(relatoLines, M + 6, y + 5)
  y += relatoH + 8

  // Fotos
  if (fotos.length > 0) {
    y = secTitle(doc, `FOTOGRAFIAS ANEXADAS (${fotos.length} foto${fotos.length > 1 ? 's' : ''})`, y)

    const cols  = 3
    const imgW  = (W - M * 2 - (cols - 1) * 4) / cols
    const imgH  = imgW * 0.75

    for (let i = 0; i < fotos.length; i++) {
      const col  = i % cols
      const row  = Math.floor(i / cols)
      const imgX = M + col * (imgW + 4)
      const imgY = y + row * (imgH + 8)

      if (imgY + imgH > PAGE_H - 18) break

      draw(doc, C_AZUL_BORDA)
      doc.setLineWidth(0.15)
      fill(doc, C_CINZA_100)
      doc.rect(imgX, imgY, imgW, imgH, 'FD')
      try {
        doc.addImage(fotos[i], 'JPEG', imgX + 0.5, imgY + 0.5, imgW - 1, imgH - 1)
      } catch {
        try { doc.addImage(fotos[i], 'PNG', imgX + 0.5, imgY + 0.5, imgW - 1, imgH - 1) }
        catch { /* imagem indisponível */ }
      }
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(6)
      color(doc, C_CINZA_500)
      doc.text(`Foto ${i + 1}`, imgX + imgW / 2, imgY + imgH + 4, { align: 'center' })
    }
  } else {
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(7.5)
    color(doc, C_CINZA_500)
    doc.text('Nenhuma foto foi anexada a esta solicitação.', W / 2, y + 10, { align: 'center' })
  }

  drawFooter(doc, sol.protocolo, 2, totalPages)

  // ─────────────────────────────────────────────────────────────────────────
  // PÁGINA 3 — Assinaturas (sempre última)
  // ─────────────────────────────────────────────────────────────────────────
  doc.addPage()
  drawHeader(doc, cfg, 'Assinaturas e Declaração', 3, geoaDataURL)
  y = CONTENT_Y

  // Declaração
  y = secTitle(doc, 'DECLARAÇÃO DO SOLICITANTE', y)
  const declText = [
    'Declaro que todas as informações prestadas nesta solicitação são verdadeiras e estão em conformidade com a',
    'Lei Municipal nº 426, de 21 de novembro de 2018. Estou ciente de que informações falsas ou procedimentos',
    'realizados sem autorização estarão sujeitos às penalidades previstas na legislação municipal.',
  ]
  const declH = declText.length * 4.5 + 8
  fill(doc, C_CINZA_100)
  draw(doc, C_CINZA_200)
  doc.setLineWidth(0.12)
  doc.rect(M, y, W - M * 2, declH, 'FD')
  fill(doc, C_AZUL)
  doc.rect(M, y, 2.5, declH, 'F')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  color(doc, C_CINZA_700)
  let dy = y + 5
  for (const line of declText) {
    doc.text(line, M + 6, dy)
    dy += 4.5
  }
  y += declH + 8

  // Identificação
  y = secTitle(doc, 'IDENTIFICAÇÃO DO PROCESSO', y)
  y1 = y; y2 = y
  y1 = field(doc, 'Protocolo', sol.protocolo, M, y1, COL_W)
  y2 = field(doc, 'Status', sol.status, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 2
  y1 = y; y2 = y
  y1 = field(doc, 'Solicitante', sol.nomeCompleto, M, y1, COL_W)
  y2 = field(doc, 'CPF', sol.cpf, M + COL_W + COL_GAP, y2, COL_W)
  y = Math.max(y1, y2) + 6

  // Linha pontilhada
  draw(doc, C_AZUL_BORDA)
  doc.setLineWidth(0.08)
  for (let px = M; px < W - M; px += 4) {
    doc.line(px, y, px + 2, y)
  }
  y += 6

  // Assinaturas
  y = secTitle(doc, 'ASSINATURAS', y)
  y += 4
  drawAssinaturas(doc, sol, y)

  drawFooter(doc, sol.protocolo, 3, totalPages)

  doc.save(`Solicitacao_${sol.protocolo}.pdf`)
}
